import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, InjectionToken, Input } from '@angular/core';

import { PushPipe } from '@ngrx/component';

import { I18nModule } from '@recruitee/i18n';
import { ThemeName } from '@recruitee/theme';
import { IconComponent, IconHelpComponent, RtTooltip } from '@recruitee/user-interface';

export interface ThemeCardData {
  title: string;
  subtitle?: string;
  theme: ThemeName;
  hasIconHelp?: boolean;
  iconHelpTooltip?: string;
}

type ThemeLayoutsAssetsProvider = (name: 'light' | 'dark' | 'system') => Promise<string>;

const THEME_LAYOUTS_ASSETS_PROVIDER = new InjectionToken<ThemeLayoutsAssetsProvider>(
  'THEME_LAYOUTS_ASSETS_PROVIDER',
);

export const provideLayoutAssetsResolver = (provider: ThemeLayoutsAssetsProvider) => ({
  provide: THEME_LAYOUTS_ASSETS_PROVIDER,
  useValue: provider,
});

@Component({
  standalone: true,
  selector: 'rt-theme-card',
  templateUrl: './theme-card.component.html',
  styleUrls: ['./theme-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [I18nModule, IconHelpComponent, IconComponent, RtTooltip, NgClass, PushPipe],
  host: {
    '[class.active]': 'isActive',
  },
})
export class ThemeCardComponent {
  @Input() public title: string;
  @Input() public theme: ThemeName;
  @Input() public subtitle: string | undefined;
  @Input() public isActive: boolean;
  @Input() public iconHelpTooltip: string | undefined;
  @Input() public width: number | undefined;
  @Input() public height: number | undefined;
  @Input() public backgroundSize: 'auto' | 'contain';

  public assetsProvider = inject(THEME_LAYOUTS_ASSETS_PROVIDER);

  public imgSrcMap = {
    light: this.resolveBgImage('light'),
    dark: this.resolveBgImage('dark'),
    auto: this.resolveBgImage('system'),
  };

  private resolveBgImage(name: Parameters<ThemeLayoutsAssetsProvider>[0]) {
    return this.assetsProvider(name).then(path => `url(${path})`);
  }
}
